/**
 * Badge tone maps.
 *
 * Every class string below is written out LITERALLY. Tailwind scans source for
 * complete class names, so building one by interpolation
 * (`text-fh-badge-${name}-text`) compiles to nothing and the badge renders unstyled.
 * That rule is inherited from the source design kit and is the single easiest thing
 * to break here.
 *
 * Colour carries meaning in this system — the surrounding palette is near
 * monochrome, so a coloured badge is always saying something.
 */

export interface Tone {
  fg: string;
  bg: string;
  /** 1px inset stroke, as a box-shadow rather than a border. */
  stroke: string;
  dot: string;
}

export type ToneMap = Record<string, Tone>;

export const BADGE_TONE = {
  red: {
    fg: "text-fh-badge-red-text",
    bg: "bg-fh-badge-red-bg",
    stroke: "shadow-[inset_0_0_0_1px_var(--color-fh-badge-red-stroke)]",
    dot: "bg-fh-badge-red-text",
  },
  blue: {
    fg: "text-fh-badge-blue-text",
    bg: "bg-fh-badge-blue-bg",
    stroke: "shadow-[inset_0_0_0_1px_var(--color-fh-badge-blue-stroke)]",
    dot: "bg-fh-badge-blue-text",
  },
  neutral: {
    fg: "text-fh-badge-neutral-text",
    bg: "bg-fh-badge-neutral-bg",
    stroke: "shadow-[inset_0_0_0_1px_var(--color-fh-badge-neutral-stroke)]",
    dot: "bg-fh-badge-neutral-text",
  },
  amber: {
    fg: "text-fh-badge-amber-text",
    bg: "bg-fh-badge-amber-bg",
    stroke: "shadow-[inset_0_0_0_1px_var(--color-fh-badge-amber-stroke)]",
    dot: "bg-fh-badge-amber-text",
  },
  green: {
    fg: "text-fh-badge-green-text",
    bg: "bg-fh-badge-green-bg",
    stroke: "shadow-[inset_0_0_0_1px_var(--color-fh-badge-green-stroke)]",
    dot: "bg-fh-badge-green-text",
  },
  gold: {
    fg: "text-fh-badge-gold-text",
    bg: "bg-fh-badge-gold-bg",
    stroke: "shadow-[inset_0_0_0_1px_var(--color-fh-badge-gold-stroke)]",
    dot: "bg-fh-badge-gold-text",
  },
  purple: {
    fg: "text-fh-badge-purple-text",
    bg: "bg-fh-badge-purple-bg",
    stroke: "shadow-[inset_0_0_0_1px_var(--color-fh-badge-purple-stroke)]",
    dot: "bg-fh-badge-purple-text",
  },
} satisfies Record<string, Tone>;

/** Employee ID chips, matching the source kit's ticket-number treatment. */
export const ID_TONE: Tone = BADGE_TONE.gold;

/** Leave / WFH / half-day request status. */
export const REQUEST_TONE: ToneMap = {
  pending: BADGE_TONE.amber,
  approved: BADGE_TONE.green,
  rejected: BADGE_TONE.red,
  cancelled: BADGE_TONE.neutral,
};

export const REIMBURSEMENT_TONE: ToneMap = {
  submitted: BADGE_TONE.blue,
  "under-review": BADGE_TONE.amber,
  approved: BADGE_TONE.green,
  rejected: BADGE_TONE.red,
  paid: { ...BADGE_TONE.green, dot: "bg-fh-positive" },
};

export const EMPLOYMENT_TONE: ToneMap = {
  active: BADGE_TONE.green,
  probation: BADGE_TONE.amber,
  suspended: BADGE_TONE.red,
  "notice-period": BADGE_TONE.amber,
  offboarded: BADGE_TONE.neutral,
};

/**
 * Document scan status. `pending` is amber rather than neutral on purpose: the
 * download is blocked until it clears, so it is a state the user is waiting on
 * rather than a passive one.
 */
export const REVIEW_TONE: ToneMap = {
  pending: BADGE_TONE.neutral,
  "self-review": BADGE_TONE.blue,
  "manager-review": BADGE_TONE.purple,
  "awaiting-acknowledgement": BADGE_TONE.gold,
  locked: BADGE_TONE.green,
};

export const PAYMENT_TONE: ToneMap = {
  unpaid: BADGE_TONE.red,
  "partially-paid": BADGE_TONE.amber,
  paid: BADGE_TONE.green,
};

export const EMPLOYMENT_TYPE_TONE: ToneMap = {
  "full-time": BADGE_TONE.blue,
  "part-time": BADGE_TONE.purple,
  contract: BADGE_TONE.amber,
  intern: BADGE_TONE.gold,
  consultant: BADGE_TONE.neutral,
};
